import type {
  AircraftClassificationResult,
  ApiErrorResult,
  ApiResult,
  PricingAddon,
  PricingAiCreditTier,
  PricingAiTransactionRate,
  PricingApiSpec,
  PricingAircraftBand,
  PricingCalculationRule,
  PricingCapability,
  PricingDatasetKey,
  PricingDatasetEnvelope,
  PricingDependencyRule,
  PricingEntitlement,
  PricingEvaluateRequest,
  PricingEvaluateResult,
  ArchitectureResponse,
  PricingMasterManifest,
  PricingOperatorProfile,
  PricingProduct,
  PricingQuoteQualifier,
  PricingReferenceData,
  PricingSku,
  PricingSummary,
  PricingUnitOfMeasure,
  PricingVersionCurrent,
} from "../contracts/pricing";

const apiBase = (import.meta.env.VITE_API_BASE_URL || "").trim();
let apiTokenProvider: (() => Promise<string>) | null = null;

function url(path: string): string {
  if (!apiBase) {
    return path;
  }
  return `${apiBase.replace(/\/$/, "")}${path}`;
}

export function setApiTokenProvider(provider: (() => Promise<string>) | null): void {
  apiTokenProvider = provider;
}

async function withAuthHeaders(init?: HeadersInit): Promise<HeadersInit> {
  const headers = new Headers(init);
  if (apiTokenProvider) {
    const token = (await apiTokenProvider()).trim();
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }
  }
  return headers;
}

async function getJson<T>(path: string): Promise<T> {
  const response = await fetch(url(path), {
    headers: await withAuthHeaders(),
  });
  if (!response.ok) {
    const maybeError = (await response.json().catch(() => null)) as ApiErrorResult | null;
    throw new Error(maybeError?.error ?? `Request failed: ${response.status}`);
  }
  return (await response.json()) as T;
}

async function postJson<TResponse, TBody>(path: string, body: TBody): Promise<TResponse> {
  const headers = await withAuthHeaders({ "Content-Type": "application/json" });
  const response = await fetch(url(path), {
    method: "POST",
    headers,
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const maybeError = (await response.json().catch(() => null)) as ApiErrorResult | null;
    throw new Error(maybeError?.error ?? `Request failed: ${response.status}`);
  }
  return (await response.json()) as TResponse;
}

export async function fetchPricingSpec(): Promise<PricingApiSpec> {
  return getJson<PricingApiSpec>("/api/pricing/spec");
}

export async function fetchPricingManifest(): Promise<PricingMasterManifest> {
  const payload = await getJson<ApiResult<PricingMasterManifest>>("/api/pricing/manifest");
  return payload.result;
}

export async function fetchPricingProducts(input?: {
  activeOnly?: boolean;
  productCode?: string;
}): Promise<PricingDatasetEnvelope<PricingProduct>> {
  const search = new URLSearchParams();
  if (typeof input?.activeOnly === "boolean") {
    search.set("activeOnly", String(input.activeOnly));
  }
  if (input?.productCode) {
    search.set("productCode", input.productCode);
  }
  const suffix = search.toString() ? `?${search.toString()}` : "";
  const payload = await getJson<ApiResult<PricingDatasetEnvelope<PricingProduct>>>(`/api/pricing/products${suffix}`);
  return payload.result;
}

export async function fetchPricingSkus(input?: {
  productCode?: string;
  skuType?: string;
}): Promise<PricingDatasetEnvelope<PricingSku>> {
  const search = new URLSearchParams();
  if (input?.productCode) {
    search.set("productCode", input.productCode);
  }
  if (input?.skuType) {
    search.set("skuType", input.skuType);
  }
  const suffix = search.toString() ? `?${search.toString()}` : "";
  const payload = await getJson<ApiResult<PricingDatasetEnvelope<PricingSku>>>(`/api/pricing/skus${suffix}`);
  return payload.result;
}

export async function fetchPricingSkuByCode(skuCode: string): Promise<PricingSku> {
  const payload = await getJson<ApiResult<PricingSku>>(`/api/pricing/skus/${encodeURIComponent(skuCode)}`);
  return payload.result;
}

export async function fetchPricingAiCreditTiers(): Promise<PricingDatasetEnvelope<PricingAiCreditTier>> {
  const payload = await getJson<ApiResult<PricingDatasetEnvelope<PricingAiCreditTier>>>(
    "/api/pricing/ai-credit-tiers",
  );
  return payload.result;
}

export async function fetchPricingSummary(): Promise<PricingSummary> {
  const payload = await getJson<ApiResult<PricingSummary>>("/api/pricing/summary");
  return payload.result;
}

export async function fetchPricingDatasetKeys(): Promise<PricingDatasetKey[]> {
  const payload = await getJson<ApiResult<PricingDatasetKey[]>>("/api/pricing/datasets");
  return payload.result;
}

type DatasetTypeMap = {
  products: PricingProduct;
  skus: PricingSku;
  capabilities: PricingCapability;
  entitlements: PricingEntitlement;
  dependencyRules: PricingDependencyRule;
  aircraftBands: PricingAircraftBand;
  operatorProfiles: PricingOperatorProfile;
  aiTransactions: PricingAiTransactionRate;
  aiCreditTiers: PricingAiCreditTier;
  addons: PricingAddon;
  unitsOfMeasure: PricingUnitOfMeasure;
  quoteQualifiers: PricingQuoteQualifier;
  calculationRules: PricingCalculationRule;
  referenceData: PricingReferenceData;
};

export async function fetchPricingDataset<TKey extends PricingDatasetKey>(
  datasetKey: TKey,
): Promise<PricingDatasetEnvelope<DatasetTypeMap[TKey]>> {
  const payload = await getJson<ApiResult<PricingDatasetEnvelope<DatasetTypeMap[TKey]>>>(
    `/api/pricing/datasets/${encodeURIComponent(datasetKey)}`,
  );
  return payload.result;
}

export async function fetchPricingMasterCurrentVersion(): Promise<PricingVersionCurrent> {
  const payload = await getJson<ApiResult<PricingVersionCurrent>>("/api/pricing-master/versions/current");
  return payload.result;
}

export async function fetchPricingMasterVersion(version: string): Promise<PricingMasterManifest> {
  const payload = await getJson<ApiResult<PricingMasterManifest>>(
    `/api/pricing-master/versions/${encodeURIComponent(version)}`,
  );
  return payload.result;
}

export async function fetchPricingMasterAiTransactions(): Promise<PricingDatasetEnvelope<PricingAiTransactionRate>> {
  const payload = await getJson<ApiResult<PricingDatasetEnvelope<PricingAiTransactionRate>>>(
    "/api/pricing-master/ai-transactions",
  );
  return payload.result;
}

export async function fetchAircraftClassification(bandCode: string): Promise<AircraftClassificationResult> {
  const search = new URLSearchParams({ bandCode });
  const payload = await getJson<ApiResult<AircraftClassificationResult>>(
    `/api/pricing-master/aircraft/classify?${search.toString()}`,
  );
  return payload.result;
}

export async function evaluatePricing(input: PricingEvaluateRequest): Promise<PricingEvaluateResult> {
  const payload = await postJson<ApiResult<PricingEvaluateResult>, PricingEvaluateRequest>(
    "/api/pricing-master/evaluate",
    input,
  );
  return payload.result;
}

export async function fetchArchitecture(): Promise<ArchitectureResponse> {
  const payload = await getJson<ApiResult<ArchitectureResponse>>("/api/architecture");
  return payload.result;
}

export async function fetchPublicArchitecture(): Promise<ArchitectureResponse> {
  const payload = await getJson<ApiResult<ArchitectureResponse>>("/public/api/architecture");
  return payload.result;
}

export async function evaluatePublicPricing(input: PricingEvaluateRequest): Promise<PricingEvaluateResult> {
  const payload = await postJson<ApiResult<PricingEvaluateResult>, PricingEvaluateRequest>("/public/api/estimate", input);
  return payload.result;
}